import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

// =============================================================================
// Types
// =============================================================================

export type AppointmentStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed'
export type AppointmentType = 'phone' | 'in_person' | 'video'
export type AppointmentStatusFilter = 'upcoming' | 'past' | 'cancelled'
export type AppointmentCalendarProvider = 'google' | 'microsoft' | 'square' | 'wix'
export type ConferenceProvider = 'meet' | 'teams'

export interface Appointment {
  id: number
  ai_agent: number | null
  ai_agent_name: string | null
  created_by: number | null
  created_by_name: string | null
  call_log: number | null
  google_calendar: number | null
  microsoft_calendar: number | null
  square_booking: number | null
  wix_booking: number | null
  calendar_name: string | null
  google_event_id: string | null
  microsoft_event_id: string | null
  square_booking_event_id: string | null
  wix_booking_event_id: string | null
  client_name: string
  client_phone: string
  client_email: string | null
  start_time: string
  end_time: string
  notes: string
  status: AppointmentStatus
  appointment_type: AppointmentType
  location: string
  conference_url: string | null
  conference_provider: ConferenceProvider | null
  conference_data: Record<string, unknown> | null
  sms_sent: boolean
  sms_sent_at: string | null
  sms_sid: string | null
  created_at: string
  updated_at: string
}

export interface AppointmentsListFilters {
  status: AppointmentStatusFilter
  page: number
  pageSize: number
  search: string
  /** '' = tous providers ; sinon filtre côté backend. */
  provider: '' | AppointmentCalendarProvider
}

export interface AppointmentsListResponse {
  results: Appointment[]
  count: number
  page: number
  page_size: number
  total_pages: number
  today_count: number
  week_count: number
  cancelled_count: number
}

// =============================================================================
// Queries
// =============================================================================

/**
 * GET /api/companies/{id}/appointments/ — liste paginée + KPIs agrégés.
 * Les KPIs (today/week/cancelled) sont calculés côté backend sur l'ensemble
 * des RDV de l'entreprise, indépendamment des filtres de la requête.
 */
export function useAppointments(
  companyId: number | undefined,
  filters: AppointmentsListFilters,
) {
  return useQuery({
    queryKey: ['appointments', companyId, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        status: filters.status,
        page: String(filters.page),
        page_size: String(filters.pageSize),
      })
      if (filters.search) params.set('search', filters.search)
      if (filters.provider) params.set('calendar_provider', filters.provider)

      const { data } = await api.get<AppointmentsListResponse>(
        `/api/companies/${companyId}/appointments/?${params.toString()}`,
      )
      return data
    },
    enabled: !!companyId,
    staleTime: 15_000,
  })
}

// =============================================================================
// Mutations
// =============================================================================

export interface CreateAppointmentInput {
  calendar_id: number
  calendar_provider: AppointmentCalendarProvider
  client_name: string
  client_phone: string
  client_email?: string
  /** ISO 8601 datetime (avec ou sans timezone). */
  start_time: string
  duration: number
  notes?: string
  appointment_type: AppointmentType
  location?: string
  send_sms?: boolean
  sms_from_number?: string
  send_email?: boolean
  send_calendar_invite?: boolean
}

export interface CreateAppointmentResponse extends Appointment {
  /** Présent quand le RDV vidéo a été créé mais sans lien Meet/Teams. */
  warning?: 'video_link_unavailable'
}

/** Erreur lancée par useCreateAppointment quand le créneau est déjà pris (HTTP 409). */
export class AppointmentSlotUnavailableError extends Error {
  constructor(message = 'Slot unavailable') {
    super(message)
    this.name = 'AppointmentSlotUnavailableError'
  }
}

export function useCreateAppointment(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateAppointmentInput) => {
      try {
        const { data } = await api.post<CreateAppointmentResponse>(
          `/api/companies/${companyId}/appointments/create/`,
          input,
        )
        return data
      } catch (err: unknown) {
        // Le backend renvoie 409 + { code: 'slot_unavailable' }
        // On le remonte typé pour que l'UI puisse afficher un message dédié.
        const e = err as {
          response?: { status?: number; data?: { code?: string } }
        }
        if (
          e?.response?.status === 409 &&
          e?.response?.data?.code === 'slot_unavailable'
        ) {
          throw new AppointmentSlotUnavailableError()
        }
        throw err
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', companyId] })
    },
  })
}

export type UpdateAppointmentInput = Partial<
  Pick<
    Appointment,
    | 'client_name'
    | 'client_phone'
    | 'client_email'
    | 'notes'
    | 'appointment_type'
    | 'location'
  >
> & {
  start_time?: string
  duration?: number
}

export function useUpdateAppointment(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      appointmentId: number
      patch: UpdateAppointmentInput
    }) => {
      const { data } = await api.patch<Appointment>(
        `/api/companies/${companyId}/appointments/${input.appointmentId}/`,
        input.patch,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', companyId] })
    },
  })
}

export function useCancelAppointment(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (appointmentId: number) => {
      await api.delete(
        `/api/companies/${companyId}/appointments/${appointmentId}/delete/`,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', companyId] })
    },
  })
}
